import { Alert, Box, Button, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useContext, useMemo, useState } from "react";
import UserContext from "../../../contexts/UserContext";
import Loader from "../../../components/common/Loader";
import DeleteModal from "../../../components/DeleteModal";
import PlaylistModal from "../../../components/PlaylistModal";
import LibraryEmptyState from "../components/LibraryEmptyState";
import LibraryPlaylistsList from "../components/LibraryPlaylistsList";
import {
  createLibraryPlaylist,
  deleteLibraryPlaylist,
  updateLibraryPlaylist,
} from "../api/libraryApi";
import useLibraryPlaylists from "../hooks/useLibraryPlaylists";
import useLibraryPageState from "../hooks/useLibraryPageState";
import { isDjUser } from "../helpers/libraryValidators";

export default function LibraryPage() {
  const theme = useTheme();
  const isPhoneView = useMediaQuery(theme.breakpoints.down("md"));
  const { user } = useContext(UserContext);
  const isAllowed = useMemo(() => isDjUser(user), [user]);
  const { playlists, isLoading, loadError, loadPlaylists } = useLibraryPlaylists();
  const [editingPlaylist, setEditingPlaylist] = useState(undefined);
  const [deletingPlaylist, setDeletingPlaylist] = useState(undefined);
  const {
    isSaving,
    setIsSaving,
    saveError,
    setSaveError,
    isEditorOpen,
    openEditor,
    closeEditor,
  } = useLibraryPageState();

  const handleCreatePlaylist = () => {
    setEditingPlaylist(undefined);
    openEditor();
  };

  const handleEditPlaylist = (playlist) => {
    setEditingPlaylist(playlist);
    openEditor();
  };

  const handleDeletePlaylist = (playlist) => {
    setDeletingPlaylist(playlist);
    setSaveError("");
  };

  const handleCloseEditor = () => {
    setEditingPlaylist(undefined);
    closeEditor();
  };

  const handleUpsertConfirm = async (body) => {
    setIsSaving(true);
    setSaveError("");
    try {
      if (editingPlaylist?.id) {
        await updateLibraryPlaylist(editingPlaylist.id, body);
      } else {
        await createLibraryPlaylist(body);
      }
      handleCloseEditor();
      await loadPlaylists();
      return undefined;
    } catch (error) {
      const message =
        typeof error === "string"
          ? error
          : "Failed to create playlist in My Playlists.";
      setSaveError(message);
      return message;
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingPlaylist?.id) {
      return;
    }

    setIsSaving(true);
    setSaveError("");
    try {
      await deleteLibraryPlaylist(deletingPlaylist.id);
      setDeletingPlaylist(undefined);
      await loadPlaylists();
    } catch (error) {
      const message =
        typeof error === "string"
          ? error
          : "Failed to delete playlist from My Playlists.";
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isAllowed) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        p: 4,
        maxWidth: "1100px",
        margin: "0 auto",
        paddingTop: "100px",
        gap: 2,
      }}
    >
      {isPhoneView && (
        <Typography variant="h4" color="white">
          My Playlists
        </Typography>
      )}
      {!isLoading && !loadError && playlists.length > 0 && (
        <Button variant="contained" onClick={handleCreatePlaylist} disabled={isSaving}>
          Create Playlist
        </Button>
      )}
      {isLoading && <Loader />}
      {!isLoading && loadError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={loadPlaylists}>
              Retry
            </Button>
          }
          sx={{ width: "100%" }}
        >
          {loadError}
        </Alert>
      )}
      {!isLoading && !loadError && playlists.length === 0 && (
        <LibraryEmptyState onCreatePlaylist={handleCreatePlaylist} />
      )}
      {!isLoading && !loadError && playlists.length > 0 && (
        <LibraryPlaylistsList
          playlists={playlists}
          onEdit={handleEditPlaylist}
          onDelete={handleDeletePlaylist}
        />
      )}
      {saveError && (
        <Alert severity="error" sx={{ width: "100%" }}>
          {saveError}
        </Alert>
      )}
      {isEditorOpen && (
        <PlaylistModal
          title={
            editingPlaylist
              ? "Edit Playlist in My Playlists"
              : "Create Playlist in My Playlists"
          }
          isLibraryEditor
          showNameField
          nameLabel="Playlist Name"
          namePlaceholder="My Afro House Set"
          playlist={editingPlaylist}
          venueCurrency="EUR"
          handleConfirm={handleUpsertConfirm}
          handleClose={handleCloseEditor}
          eventDate={new Date().toISOString().slice(0, 10)}
        />
      )}
      {deletingPlaylist && (
        <DeleteModal
          isPlaylist={true}
          name={deletingPlaylist.name}
          handleConfirm={handleDeleteConfirm}
          handleClose={() => setDeletingPlaylist(undefined)}
        />
      )}
    </Box>
  );
}
